import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api.js'
import { Card, Field, FileField, Alert, Advanced, Modal } from '../../lib.jsx'
import { IMPORT_AI_PROMPT } from './importPrompt.js'

const STATUS_PL = { draft: 'roboczy', published: 'opublikowany' }

function UploadForm({ onDone }) {
  const [name, setName] = useState('')
  const [documentType, setDocumentType] = useState('ebook')
  const [pageSize, setPageSize] = useState('A4')
  const [orientation, setOrientation] = useState('portrait')
  const [compositionMode, setCompositionMode] = useState('single_page')
  const [imageStylePrompt, setImageStylePrompt] = useState('')
  const [imageColorPrompt, setImageColorPrompt] = useState('')
  const [generalAssumptions, setGeneralAssumptions] = useState('')
  const [file, setFile] = useState(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const sectioned = compositionMode === 'sectioned'

  const submit = async (e) => {
    e.preventDefault()
    if (!file) return
    setErr(''); setBusy(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const params = { name, document_type: documentType, page_size: pageSize, orientation, composition_mode: compositionMode }
      if (sectioned) {
        params.image_style_prompt = imageStylePrompt
        params.image_color_prompt = imageColorPrompt
        params.general_assumptions = generalAssumptions
      }
      const tpl = await api.uploadTemplate(fd, params)
      onDone(tpl.id)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit}>
      <Alert kind="err">{err}</Alert>
      <Field label="Nazwa"><input value={name} onChange={e => setName(e.target.value)} required /></Field>
      <div className="row">
        <Field label="Typ dokumentu">
          <select value={documentType} onChange={e => setDocumentType(e.target.value)}>
            <option value="ebook">ebook / dokument</option>
            <option value="raport">raport</option>
            <option value="prezentacja">prezentacja</option>
            <option value="ulotka">ulotka</option>
          </select>
        </Field>
        <Field label="Format strony">
          <select value={pageSize} onChange={e => setPageSize(e.target.value)}>
            <option value="A4">A4</option>
            <option value="Letter">Letter</option>
            <option value="16:9">16:9 (slajd)</option>
            <option value="4:3">4:3 (slajd)</option>
          </select>
        </Field>
        <Field label="Orientacja">
          <select value={orientation} onChange={e => setOrientation(e.target.value)}>
            <option value="portrait">pionowa</option>
            <option value="landscape">pozioma</option>
          </select>
        </Field>
      </div>
      <Field label="Tryb kompozycji"
        hint={sectioned
          ? 'Ebook/prezentacja — pliki templates/*.html to ROZDZIAŁY (nie pojedyncze strony): każdy rozlewa się na tyle fizycznych stron, ile potrzeba, wypełniany niezależnie dobieranymi sekcjami z partials_templates/.'
          : 'Broszura/ulotka — dzisiejszy model: jeden plik HTML = jedna cała, fizyczna strona.'}>
        <select value={compositionMode} onChange={e => setCompositionMode(e.target.value)}>
          <option value="single_page">Broszura / ulotka (jeden plik = jedna strona)</option>
          <option value="sectioned">Ebook / prezentacja (pliki per rozdział, złożone z sekcji)</option>
        </select>
      </Field>
      {sectioned && (
        <Advanced label="Prompty ogólne szablonu (styl/kolorystyka/założenia)">
          <Field label="Ogólne założenia"><textarea value={generalAssumptions} onChange={e => setGeneralAssumptions(e.target.value)} rows={3} /></Field>
          <Field label="Stylistyka zdjęć"><textarea value={imageStylePrompt} onChange={e => setImageStylePrompt(e.target.value)} rows={2} /></Field>
          <Field label="Kolorystyka"><textarea value={imageColorPrompt} onChange={e => setImageColorPrompt(e.target.value)} rows={2} /></Field>
        </Advanced>
      )}
      <FileField label="Paczka ZIP szablonu" accept=".zip" file={file} onPick={setFile}
        hint={sectioned
          ? 'templates/*.html = rozdziały (wymagany kontener <div data-sections-area>); partials_templates/global_header*/global_footer* = nagłówek/stopka; reszta partials_templates/* = sekcje.'
          : 'HTML/CSS partiali ze znacznikami data-content / data-pptx-* wg specyfikacji.'} />
      <button className="btn btn-primary" disabled={busy || !file}>{busy ? 'Importowanie…' : 'Importuj ZIP'}</button>
    </form>
  )
}

function AiDesignForm({ onDone }) {
  const [name, setName] = useState('')
  const [documentType, setDocumentType] = useState('ebook')
  const [pageSize, setPageSize] = useState('A4')
  const [orientation, setOrientation] = useState('portrait')
  const [layoutsText, setLayoutsText] = useState('okladka\ntytul-tresc\nlista-punktow\ncytat\npodsumowanie')
  const [brand, setBrand] = useState('')
  const [style, setStyle] = useState('')
  const [colors, setColors] = useState('')
  const [fonts, setFonts] = useState('')
  const [notes, setNotes] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [jobId, setJobId] = useState(null)

  const submit = async (e) => {
    e.preventDefault()
    const layouts = layoutsText.split('\n').map(s => s.trim()).filter(Boolean)
    if (!layouts.length) { setErr('Podaj co najmniej jeden layout (jeden na linię)'); return }
    setErr(''); setBusy(true)
    try {
      const res = await api.designTemplate({
        name, document_type: documentType, page_size: pageSize, orientation,
        layouts, brand, style_description: style, colors, fonts, additional_requirements: notes,
      })
      setJobId(res.job_id)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  if (jobId) return <JobWait jobId={jobId} label="AI projektuje szablon…" onDone={() => onDone()} />

  return (
    <form onSubmit={submit}>
      <Alert kind="err">{err}</Alert>
      <Field label="Nazwa"><input value={name} onChange={e => setName(e.target.value)} required /></Field>
      <div className="row">
        <Field label="Typ dokumentu">
          <select value={documentType} onChange={e => setDocumentType(e.target.value)}>
            <option value="ebook">ebook / dokument</option>
            <option value="raport">raport</option>
            <option value="prezentacja">prezentacja</option>
            <option value="ulotka">ulotka</option>
          </select>
        </Field>
        <Field label="Format strony">
          <select value={pageSize} onChange={e => setPageSize(e.target.value)}>
            <option value="A4">A4</option>
            <option value="Letter">Letter</option>
            <option value="16:9">16:9 (slajd)</option>
            <option value="4:3">4:3 (slajd)</option>
          </select>
        </Field>
        <Field label="Orientacja">
          <select value={orientation} onChange={e => setOrientation(e.target.value)}>
            <option value="portrait">pionowa</option>
            <option value="landscape">pozioma</option>
          </select>
        </Field>
      </div>
      <Field label="Layouty do wygenerowania" hint="Jeden slug na linię — każdy to osobne wywołanie AI.">
        <textarea value={layoutsText} onChange={e => setLayoutsText(e.target.value)} rows={5} />
      </Field>
      <Advanced label="Brief marki (opcjonalnie)">
        <Field label="Marka / firma"><input value={brand} onChange={e => setBrand(e.target.value)} /></Field>
        <Field label="Opis stylu"><textarea value={style} onChange={e => setStyle(e.target.value)} placeholder="np. minimalistyczny, korporacyjny, dużo białej przestrzeni" /></Field>
        <div className="row">
          <Field label="Kolory"><input value={colors} onChange={e => setColors(e.target.value)} placeholder="np. #1F5C52, #A85A34" /></Field>
          <Field label="Fonty"><input value={fonts} onChange={e => setFonts(e.target.value)} placeholder="np. Georgia + Inter" /></Field>
        </div>
        <Field label="Dodatkowe wymagania"><textarea value={notes} onChange={e => setNotes(e.target.value)} /></Field>
      </Advanced>
      <button className="btn btn-primary" disabled={busy}>{busy ? 'Wysyłanie…' : 'Generuj szablon z AI'}</button>
    </form>
  )
}

function JobWait({ jobId, label, onDone }) {
  const [job, setJob] = useState(null)
  const [err, setErr] = useState('')
  useEffect(() => {
    let cancelled = false, t
    const tick = async () => {
      try {
        const j = await api.job(jobId)
        if (cancelled) return
        setJob(j)
        if (j.status === 'done') { onDone(j); return }
        if (j.status === 'error') { setErr(j.error_msg || 'Zadanie zakończone błędem'); return }
      } catch { /* ponów */ }
      if (!cancelled) t = setTimeout(tick, 2500)
    }
    tick()
    return () => { cancelled = true; clearTimeout(t) }
  }, [jobId])
  return (
    <div>
      <div className="spin" style={{ marginBottom: 14 }} />
      <div className="muted small" style={{ marginBottom: 10 }}>{label}</div>
      {job && <div className="pill" style={{ marginBottom: 10 }}>{job.status}</div>}
      <Alert kind="err">{err}</Alert>
    </div>
  )
}

function ImportPromptModal({ onClose }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(IMPORT_AI_PROMPT)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Modal title="Prompt dla AI — przygotowanie plików do importu" onClose={onClose}>
      <div className="muted small" style={{ marginBottom: 14 }}>
        Skopiuj poniższy prompt i wklej go do dowolnego AI (ChatGPT, Claude…) razem ze swoim
        materiałem (mockup/branding/opis) — dostaniesz pliki HTML/CSS gotowe do spakowania w ZIP
        dokładnie w formacie, jakiego wymaga import.
      </div>
      <textarea readOnly value={IMPORT_AI_PROMPT} rows={16} className="mono"
        style={{ fontSize: 12, whiteSpace: 'pre-wrap' }} onClick={e => e.target.select()} />
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button type="button" className="btn btn-primary" onClick={copy}>{copied ? '✓ Skopiowano' : 'Kopiuj prompt'}</button>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Zamknij</button>
      </div>
    </Modal>
  )
}

export default function Templates() {
  const nav = useNavigate()
  const [templates, setTemplates] = useState([])
  const [mode, setMode] = useState(null) // null | 'upload' | 'ai'
  const [showPrompt, setShowPrompt] = useState(false)
  const [err, setErr] = useState('')

  const load = () => api.templates().then(setTemplates).catch(e => setErr(e.message))
  useEffect(() => { load() }, [])

  const remove = async (t) => {
    if (!window.confirm(`Usunąć szablon „${t.name}” bezpowrotnie?`)) return
    try { await api.deleteTemplate(t.id); load() } catch (e) { setErr(e.message) }
  }

  return (
    <>
      {showPrompt && <ImportPromptModal onClose={() => setShowPrompt(false)} />}
      <div className="topbar">
        <div><h1>Szablony ebooków / broszur</h1><div className="sub">Import ręczny (ZIP) lub generowanie przez AI Template Designera.</div></div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" onClick={() => setShowPrompt(true)}>📋 Prompt dla AI</button>
          <button className="btn" onClick={() => setMode(mode === 'upload' ? null : 'upload')}>{mode === 'upload' ? 'Anuluj' : '+ Wgraj ZIP'}</button>
          <button className="btn btn-primary" onClick={() => setMode(mode === 'ai' ? null : 'ai')}>{mode === 'ai' ? 'Anuluj' : '✦ Generuj z AI'}</button>
        </div>
      </div>
      <div className="content">
        <Alert kind="err">{err}</Alert>
        {mode === 'upload' && (
          <Card title="Import szablonu z ZIP" right={null}>
            <UploadForm onDone={(id) => nav(`/admin/templates/${id}`)} />
          </Card>
        )}
        {mode === 'ai' && (
          <Card title="AI Template Designer">
            <AiDesignForm onDone={() => { setMode(null); load() }} />
          </Card>
        )}
        <Card title="Wszystkie szablony" desc={`${templates.length} łącznie`}>
          {templates.length === 0 ? <div className="empty">Brak szablonów — wgraj ZIP albo wygeneruj z AI powyżej.</div> : (
            <table>
              <thead><tr><th>Nazwa</th><th>Typ</th><th>Format</th><th>Tryb</th><th>Status</th><th>Wersja</th><th /></tr></thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.id}>
                    <td><a onClick={() => nav(`/admin/templates/${t.id}`)} style={{ cursor: 'pointer', fontWeight: 700 }}>{t.name}</a></td>
                    <td>{t.document_type}</td>
                    <td>{t.page_size} · {t.orientation === 'portrait' ? 'pionowa' : 'pozioma'}</td>
                    <td><span className="pill">{t.composition_mode === 'sectioned' ? 'sekcyjny' : 'jednostronicowy'}</span></td>
                    <td><span className={`pill ${t.status === 'published' ? 'pill-done' : ''}`}>{STATUS_PL[t.status] || t.status}</span></td>
                    <td className="mono">v{t.current_version}</td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-ghost" onClick={() => nav(`/admin/templates/${t.id}`)}>Otwórz</button>
                      <button className="btn btn-sm btn-danger" onClick={() => remove(t)}>Usuń</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  )
}
